import React, { useEffect, useState } from 'react';
import { connect } from "react-redux";
import PropTypes from 'prop-types';
import 'react-intl-tel-input/dist/main.css';
import 'react-multi-email/dist/style.css';
import './assets/css/index.scss';
import './assets/css/variables.scss';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/js/bootstrap.min.js';
import { DefaultLanguage, LOCAL_STORAGE_KEYS, LOGGED_IN, RouteName, SCHEDULE_TOKEN_REFRESH_ON_LOAD } from './constants/Constants';
import { checkIsCompanyAvailable } from "./actions/onLoadFunctionAction"
import AllRoutes from './routes';
import { getDomainURL, handleConnectSignal, scheduleTokenAutoRefresh, setPublicVariables, updateDomainLogoInStorage } from "./shared/utility";
import { initializeLDFromStoredData } from "./shared/launchDarklyUtility";
import PublicVariables from './constants/PublicVariables';
import { getLanguage, setLanguage } from './actions/languageAction';
import storage from './services/storage';
import history from './history';
import PageLoader from './components/UI/PageLoader';
import InstallPrompt from './components/PWA/InstallPrompt';

export const LOCAL_DOMAIN_NAME_FOR_TESTING = 'hexor.blitzz.app';

const App = (props) => {
  let [isDomainExist, setIsDomainExist] = useState(false);

  const checkCompanyInfo = () => {
    props
      .checkIsCompanyAvailable({
        "domainURL": getDomainURL() ?? ""
      })
  }

  const fetchLanguage = () => {
    const configData = storage.get(LOCAL_STORAGE_KEYS.USER_DATA);
    if (configData) {
      props.getLanguage();
    }
    else {
      const usersLanguage = storage.get(LOCAL_STORAGE_KEYS.USERS_LANGUAGE);
      props.setLanguage(usersLanguage ? usersLanguage : DefaultLanguage, null);
    }
  }

  useEffect(() => {
    function storageChange(event) {
      if (event.key === LOGGED_IN) {
        if (event.newValue == '0') {
          window.open('', '_self', '');
          window.close();
          history.push(RouteName.LOGIN)
        }
      } else if (event.key === 'Blitzz.configdata') {
        if (event.newValue == null) { // means logout from other tabs then redirect to login state
          history.push(RouteName.LOGIN)
        }
      }
    }
    window.addEventListener('storage', storageChange);
    return () => window.addEventListener('storage', storageChange);
  })

  // iOS Safari workaround: when a Bootstrap modal opens on iPhone Safari, an
  // autofocused input inside opens the soft keyboard, which shrinks the visual
  // viewport. When the modal closes, iOS Safari doesn't cleanly restore the
  // viewport transform on the position:fixed body — the header ends up scrolled
  // off-screen. Locking body via position:fixed with the saved scrollY (and
  // restoring on close) forces iOS to reset the transform on modal close.
  // iOS-gated so desktop/Android are untouched. Autofocus is intentional (for
  // keyboard accessibility) and stays in place; this compensates for the
  // downstream iOS-only viewport quirk it triggers.
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (!isIOS) return;
    let savedScrollY = 0;
    let locked = false;
    const observer = new MutationObserver(() => {
      const isModalOpen = document.body.classList.contains('modal-open');
      if (isModalOpen && !locked) {
        savedScrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${savedScrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        locked = true;
      } else if (!isModalOpen && locked) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        // Force iOS Safari to flush any residual viewport transform left over
        // from soft-keyboard adjustment before we restore scroll.
        void document.body.offsetHeight;
        window.scrollTo(0, savedScrollY);
        locked = false;
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Sync a `data-pointer-focus` attribute on <html> with the user's current
  // input modality. Paired with a CSS rule in index.scss that suppresses the
  // focus ring on text inputs / textareas / contenteditable while the
  // attribute is present. WCAG 2.4.7 only requires a visible ring on
  // KEYBOARD focus — this quiets the ring for mouse users clicking into a
  // form field. Buttons / links / checkboxes / radios are unaffected —
  // browser's native :focus-visible already suppresses their rings on mouse
  // focus. Matches the exact pattern used in our other portals.
  useEffect(() => {
    const setPointer = () => document.documentElement.setAttribute('data-pointer-focus', '');
    // Tab, not any key: typing into a field the user had clicked would
    // otherwise flip back to "keyboard" and pop a ring open mid-sentence.
    const clearOnTab = (event) => {
      if (event.key === 'Tab') document.documentElement.removeAttribute('data-pointer-focus');
    };
    const clearOnKeyboardClick = (event) => {
      if (event.isTrusted && event.detail === 0) document.documentElement.removeAttribute('data-pointer-focus');
    };
    document.addEventListener('pointerdown', setPointer, { capture: true, passive: true });
    document.addEventListener('keydown', clearOnTab, { capture: true });
    document.addEventListener('click', clearOnKeyboardClick, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', setPointer, { capture: true });
      document.removeEventListener('keydown', clearOnTab, { capture: true });
      document.removeEventListener('click', clearOnKeyboardClick, { capture: true });
    };
  }, []);

  // WCAG 2.1 SC 1.4.13 — Esc dismisses the CSS-only .custom-tooltip
  // without moving focus. A `.custom-tooltip-hidden` class overrides both
  // :hover and :focus-within reveals and is cleared once the trigger loses focus.
  useEffect(() => {
    function handleTooltipEscape(e) {
      if (e.key !== 'Escape') return;
      const active = document.activeElement;
      if (!active || active === document.body) return;
      const radioBox = active.closest && active.closest('.radio-btn-box');
      const tooltip = (active.closest && active.closest('.custom-tooltip')) || radioBox?.querySelector('.custom-tooltip');
      if (!tooltip || tooltip.classList.contains('custom-tooltip-hidden')) return;
      const scope = tooltip.contains(active) ? tooltip : radioBox;
      tooltip.classList.add('custom-tooltip-hidden');
      const clear = () => {
        tooltip.classList.remove('custom-tooltip-hidden');
        scope.removeEventListener('focusout', onFocusOut);
      };
      const onFocusOut = (ev) => {
        if (!scope.contains(ev.relatedTarget)) clear();
      };
      scope.addEventListener('focusout', onFocusOut);
    }
    document.addEventListener('keydown', handleTooltipEscape);
    return () => document.removeEventListener('keydown', handleTooltipEscape);
  }, []);

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      scheduleTokenAutoRefresh(); // Schedule automatic token refresh on visibilityState is visible 
    }
  };

  useEffect(() => {
    if (process.env.REACT_APP_APP_ENV === "production") { // production env
      console.log = function () {
        // This is intentional
      };
      console.error = function () {
        // This is intentional
      };
      console.info = function () {
        // This is intentional
      };
      console.warn = function () {
        // This is intentional
      };
    }

    if (getDomainURL() !== null && getDomainURL() !== "") { // call api when custom url and host mapping url to check
      checkCompanyInfo();
    }
    else {
      PublicVariables.canGetLanguage = true;
      fetchLanguage();
    }

    // For FreshDesk View
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('nonce') && urlParams.has('redirect_uri') && urlParams.has('state')) {
      storage.set(LOCAL_STORAGE_KEYS.FRESH_DESK_URL, urlParams.toString())
    }

    handleConnectSignal() // handle journey editor socket connection
    scheduleTokenAutoRefresh(SCHEDULE_TOKEN_REFRESH_ON_LOAD); // Schedule automatic token refresh on initial load
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (Object.keys(props?.language?.selectedlanguage).length > 0 && !isDomainExist) {
      setIsDomainExist(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.language?.selectedlanguage]);

  useEffect(() => {
    if (props.language?.languageList?.length > 0) {
      props.setLanguage(props.language?.selectedlanguage?.languageCode, props.language?.languageList);
    }
  }, [props.language?.languageList])

  useEffect(() => { // flow of host mapping URL and Subdomain URL
    const initializeLDWithCompanyData = async () => {
      const companyData = props.isCompanyAvailableData;
      if (companyData) {
        if (companyData.customHostMappingUrl && (window.location.origin === companyData.customHostMappingUrl) && !companyData.hasSSL) { // If hostmapping URL then only check SSL certificate
          alert(`Redirecting to '${companyData.domainUrl}' as SSL Certificate is not bind for this URL.`);
          window.location.href = companyData.domainUrl;
        } else {
          updateDomainLogoInStorage(companyData?.domainLogo)
          setPublicVariables(companyData);

          // Initialize LaunchDarkly with centralized logic
          await initializeLDFromStoredData();

          props.setLanguage(companyData.defaultLanguage, companyData.languages);
        }
      }
      if (props.isCompanyAvailableError) {
        setIsDomainExist(false);
      }
    };

    initializeLDWithCompanyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isCompanyAvailableData, props.isCompanyAvailableError])

  return (
    <>
      {isDomainExist ? (<AllRoutes />) : <PageLoader/>}
      <InstallPrompt />
    </>

  );
}

const mapStateToProps = (state) => {
  return {
    isCompanyAvailableData: state.onLoadData.isCompanyAvailableData,
    isCompanyAvailableError: state.onLoadData.isCompanyAvailableError,
    language: state.language,
  }
}

App.propTypes = {
  isCompanyAvailableData: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  isCompanyAvailableError: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  language: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  checkIsCompanyAvailable: PropTypes.func,
  setLanguage: PropTypes.func,
  getLanguage: PropTypes.func,
}

export default connect(mapStateToProps,
  {
    checkIsCompanyAvailable,
    setLanguage,
    getLanguage,
  }
)(App);
